import { useMemo } from 'react'
import { CellContext, ColumnDef } from '@tanstack/react-table'
import { Checkbox } from '@/components/shadcn/checkbox'
import { DataTableV2Action, DataTableV2GroupConfig } from '../types'
import { RowActionsV2Menu } from '../components/RowActionsV2Menu'

const ACTION_BUTTON_WIDTH = 48

function getActionsColumnWidth(rowActions: DataTableV2Action[] | undefined, hasGroupEditAction: boolean): number {
  const inlineActionCount = rowActions?.filter((action) => action.inDropdownMenu === false).length ?? 0
  const dropdownActionCount = rowActions?.filter((action) => action.inDropdownMenu !== false).length ?? 0
  const rowActionsWidth = inlineActionCount * ACTION_BUTTON_WIDTH + (dropdownActionCount > 0 ? ACTION_BUTTON_WIDTH : 0)
  const groupEditWidth = hasGroupEditAction ? ACTION_BUTTON_WIDTH : 0

  return Math.max(rowActionsWidth, groupEditWidth)
}

export function useTableColumnsV2<TData, TValue>(
  columns: ColumnDef<TData, TValue>[],
  isSelectionEnabled: boolean,
  rowActions?: DataTableV2Action[],
  groupConfig?: DataTableV2GroupConfig
) {
  return useMemo(() => {
    let selectionColumn: ColumnDef<TData, TValue> | undefined = undefined
    let actionsColumn: ColumnDef<TData, TValue> | undefined = undefined

    if (isSelectionEnabled) {
      selectionColumn = {
        id: 'select',
        size: 50,
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
            // className="translate-y-[2px]"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            // className="translate-y-[2px]"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }
    }

    if ((rowActions && rowActions.length > 0) || groupConfig?.canRegroup) {
      actionsColumn = {
        id: 'rowActions',
        cell: (info: CellContext<TData, TValue>) => (
          <RowActionsV2Menu info={info} rowActions={rowActions || []} groupConfig={groupConfig} />
        ),
        size: getActionsColumnWidth(rowActions, !!groupConfig?.canRegroup),
      }
    }

    const finalColumns = [selectionColumn, ...columns, actionsColumn].filter(
      (col): col is ColumnDef<TData, TValue> => col !== undefined
    )

    return finalColumns
  }, [columns, rowActions, groupConfig, isSelectionEnabled])
}
